/**
 * PricePanel — margin & final price control panel (F-2.0 Plan Phase 6.6,
 * extended in PR 6a Phase 5).
 *
 * Consumes useMarginCalculator to provide:
 *   - Reference total display (read-only) — switches between Rev3 / Rev4 / Custo
 *   - Reference type toggle (header) — REV4 free; Custo locked to admin
 *   - Margin slider (0–50%, marks at 0/5/10/15/25/50)
 *   - Margin inline label "X.X% (€ Y,YY)"
 *   - Final price input (editable — drives margin backwards)
 *   - Margin zone badge (loss/low/healthy/high)
 *   - Weighted Rev4 average display
 *
 * PR 6a Phase 5 additions:
 *   - REV4 ToggleButton inserted between REV3 and Custo (free for all roles).
 *   - IVA label (semantic <button> with Tooltip) shown when `vatRate` set.
 *     Replaces the previous <Box tabIndex={0}> a11y anti-pattern.
 *   - ProposalKpiBlock integrated above the header — opt-in via `role` prop.
 *
 * KPI block — internal shim (Phase 5 design decision):
 *   The hook useProposalKpis expects a canonical `ProposalResponse` with
 *   unit_price_cents + min_price_cents per line. PricePanel only knows the
 *   F2-VR shape (`Rev4Item[]` + a single `priceCents` total), so we build a
 *   synthetic shim **for the KPI block only**:
 *     - unit_price = priceCents distributed proportionally to each line's
 *       reference total (Rev3, fallback Rev4, fallback Cost). Preserves
 *       relative weights of the chosen reference.
 *     - min_price_cents = MAX(rev4 ?? rev3 ?? cost, ROUND(cost × 1.05)).
 *       Default 5% floor — we don't know the category here. The server
 *       authoritative value (per-category margin from the catalog) flows
 *       through in Phase 6 once ProposalEditor passes the real response.
 *   These KPIs are therefore a *preview* — order of magnitude correct, but
 *   not byte-identical to what the server will surface on Save. Acceptable
 *   for in-form feedback; the final view (revision page) shows server-canon.
 *
 * Invariant R1 (Phase 4 review): the `role` passed here MUST match the
 * role used to build the actual proposal response upstream. The caller is
 * responsible — typically `deriveProposalRole(useAuth().profile)`.
 *
 * F2-VR backwards compatibility: callers that don't pass `role` or `vatRate`
 * get the legacy 2-toggle / no-IVA / no-KPI surface unchanged.
 */
import React from "react";
import {
  Box,
  Slider,
  TextField,
  Typography,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from "@mui/material";
import { useMarginCalculator } from "../../hooks/useMarginCalculator";
import {
  marginZone,
  MarginZone,
  Rev4Item,
  priceFromMargin,
} from "../../helpers/calculateMargin";
import {
  ProposalReferenceType,
  ProposalRole,
  ProposalResponse,
  AdminItemDTO,
  ComercialItemDTO,
} from "../../model/Proposal";
import { useProposalKpis } from "../../hooks/useProposalKpis";
import ProposalKpiBlock from "./ProposalKpiBlock";

interface Props {
  items: Rev4Item[];
  /** When set, overrides the internally-calculated price (controlled mode). */
  externalPriceCents?: number;
  /** Called whenever the final price changes so the parent can persist it. */
  onPriceChange?: (totalCents: number) => void;
  /** Active reference type for margin calculation. Default "REV3". */
  referenceType?: ProposalReferenceType;
  /** Notifies parent that the reference type was switched in the toggle. */
  onReferenceTypeChange?: (t: ProposalReferenceType) => void;
  /** Whether the current user is admin (controls Custo toggle availability). */
  isAdmin?: boolean;
  /** Hard-disables the toggle entirely (e.g. COMMERCIAL locked to existing). */
  referenceLocked?: boolean;
  /**
   * PR 6a — JWT-derived proposal role.
   *
   * When provided, the KPI block renders above the header (opt-in surface,
   * keeps F2-VR call-sites working unchanged).
   *
   * **Invariant R1**: this MUST be the same role used to construct the
   * actual proposal response upstream.
   */
  role?: ProposalRole;
  /**
   * PR 6a — VAT rate as a decimal (0.23 = 23%). When provided, an IVA label
   * with a tooltip describing the regime is rendered next to the price.
   */
  vatRate?: number;
  /**
   * PR 6a — Optional VAT regime label (e.g. "GERAL", "MARGEM", "ISENTO").
   * Surfaced inside the IVA tooltip when present.
   */
  vatRegime?: string;
}

const SLIDER_MARKS = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 10, label: "10%" },
  { value: 15, label: "15%" },
  { value: 25, label: "25%" },
  { value: 50, label: "50%" },
];

const ZONE_COLOR: Record<MarginZone, "error" | "warning" | "success" | "info"> =
  {
    loss: "error",
    low: "warning",
    healthy: "success",
    high: "info",
  };

const ZONE_LABEL: Record<MarginZone, string> = {
  loss: "Prejuízo",
  low: "Margem baixa",
  healthy: "Margem saudável",
  high: "Margem alta",
};

/** Default min-margin floor used when no per-category data is available. */
const DEFAULT_MIN_MARGIN = 0.05;

function formatCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

function formatEuros(cents: number): string {
  // pt-PT — comma decimal separator
  return formatCents(cents).replace(".", ",");
}

function refTotalLabelFor(type: ProposalReferenceType): string {
  switch (type) {
    case "REV3":
      return "Rev3";
    case "REV4":
      return "Rev4";
    case "COST":
      return "Custo";
    default:
      return type;
  }
}

function refTotalTooltipFor(type: ProposalReferenceType): string {
  switch (type) {
    case "REV3":
      return "Total Rev3 (preço lista revendedor) — snapshot ao adicionar produtos";
    case "REV4":
      return "Total Rev4 (preço lista revendedor secundário) — snapshot ao adicionar produtos";
    case "COST":
      return "Custo total de catálogo (snapshot, só admin)";
    default:
      return "";
  }
}

/**
 * Build an internal KPI shim from F2-VR `Rev4Item[]` + a single `priceCents`
 * total. See file-header rationale; this is an approximation, not server-canon.
 *
 * Shape note: this builds objects that satisfy AdminItemDTO/ComercialItemDTO
 * structurally. Fields not used by useProposalKpis (e.g. `pvp_cents`, `name`,
 * `vat_rate`, `vat_regime`, `margin_at_risk`, `margin_pct`) are filled with
 * benign defaults — they're never displayed because the shim never leaves
 * this hook chain.
 */
function buildKpiShim(
  items: Rev4Item[],
  priceCents: number,
  referenceType: ProposalReferenceType,
  role: ProposalRole,
): ProposalResponse {
  // Reference per line — Rev3 → Rev4 → Cost — for proportional price split.
  const refPerLine = items.map(
    (it) => it.rev3_cents ?? it.rev4_cents ?? it.cost_cents,
  );
  const refTotal = refPerLine.reduce(
    (acc, ref, i) => acc + ref * items[i].quantity,
    0,
  );

  // Distribute the total price proportionally. If refTotal is 0 (degenerate
  // input), split evenly across quantities.
  const totalQty = items.reduce((a, it) => a + it.quantity, 0) || 1;

  const buildAdminItem = (it: Rev4Item, idx: number): AdminItemDTO => {
    const ref = refPerLine[idx];
    const weight =
      refTotal > 0 ? (ref * it.quantity) / refTotal : it.quantity / totalQty;
    const lineTotal = Math.round(priceCents * weight);
    const unit =
      it.quantity > 0 ? Math.round(lineTotal / it.quantity) : lineTotal;
    const minFromCost = Math.round(it.cost_cents * (1 + DEFAULT_MIN_MARGIN));
    const minFloor = it.rev4_cents ?? it.rev3_cents ?? it.cost_cents;
    const minPrice = Math.max(minFloor, minFromCost);
    const marginPct = unit > 0 ? (unit - it.cost_cents) / unit : 0;
    return {
      // Synthetic ids — never sent to wire.
      product_id: `shim-${idx}`,
      name: "",
      pvp_cents: it.pvp_cents,
      unit_price_cents: unit,
      min_price_cents: minPrice,
      margin_at_risk: unit < minPrice,
      quantity: it.quantity,
      vat_rate_pct: 0,
      vat_regime: "",
      rev3_cents: it.rev3_cents,
      rev4_cents: it.rev4_cents,
      cost_cents: it.cost_cents,
      margin_pct: marginPct,
    };
  };

  const buildComercialItem = (it: Rev4Item, idx: number): ComercialItemDTO => {
    const admin = buildAdminItem(it, idx);
    // Comercial = BaseItemDTO — strip cost_cents + margin_pct only.
    return {
      product_id: admin.product_id,
      name: admin.name,
      pvp_cents: admin.pvp_cents,
      unit_price_cents: admin.unit_price_cents,
      min_price_cents: admin.min_price_cents,
      margin_at_risk: admin.margin_at_risk,
      quantity: admin.quantity,
      vat_rate_pct: admin.vat_rate_pct,
      vat_regime: admin.vat_regime,
      rev3_cents: admin.rev3_cents,
      rev4_cents: admin.rev4_cents,
    };
  };

  // Synthetic version = priceCents. `useProposalKpis` memoizes on
  // [role, version, items.length] (oms invariant: real revisions bump version
  // whenever unit_price_cents changes). The shim mutates unit_price_cents
  // within a single logical "revision", so we use priceCents itself as a
  // monotonic identity token to invalidate the memo on every price change.
  // This convention is shim-local — real backend responses use the actual
  // revision.version. See useProposalKpis JSDoc.
  const syntheticVersion = priceCents;
  // Sentinel metadata for shim revisions. `created_by` and `created_at` are
  // required on the wire contract (BaseRevisionDTO) — when synthesising a
  // response locally for KPI computation, we emit empty strings to signal
  // "no authorship; do not display". `ProposalRevisionView` and any other
  // consumer reading these fields MUST treat "" / falsy as "absent".
  const SHIM_CREATED_BY = "";
  const SHIM_CREATED_AT = "";
  if (role === "admin") {
    return {
      role: "admin",
      revision: {
        version: syntheticVersion,
        reference_type: referenceType,
        total_cents: priceCents,
        created_by: SHIM_CREATED_BY,
        created_at: SHIM_CREATED_AT,
        items: items.map(buildAdminItem),
      },
    };
  }
  return {
    role: "comercial",
    revision: {
      version: syntheticVersion,
      reference_type: referenceType,
      total_cents: priceCents,
      created_by: SHIM_CREATED_BY,
      created_at: SHIM_CREATED_AT,
      items: items.map(buildComercialItem),
    },
  };
}

const PricePanel: React.FC<Props> = ({
  items,
  externalPriceCents,
  onPriceChange,
  referenceType = "REV3",
  onReferenceTypeChange,
  isAdmin = false,
  referenceLocked = false,
  role,
  vatRate,
  vatRegime,
}) => {
  const calc = useMarginCalculator(items, referenceType);
  const zone = marginZone(calc.margin);

  // PR 6a — KPI shim. Hook is called unconditionally to keep hook order
  // stable; we feed an empty-items response when role is absent so the hook
  // is cheap and returns EMPTY KPIs that the JSX gate suppresses anyway.
  const kpiResponse = React.useMemo<ProposalResponse>(
    () =>
      role !== undefined
        ? buildKpiShim(items, calc.priceCents, referenceType, role)
        : {
            role: "comercial",
            revision: {
              version: 0,
              reference_type: referenceType,
              total_cents: 0,
              // Empty-state shim — same sentinel convention as buildKpiShim.
              created_by: "",
              created_at: "",
              items: [],
            },
          },
    [items, calc.priceCents, referenceType, role],
  );
  const kpis = useProposalKpis(kpiResponse);

  // Sync external price into calculator when it changes (e.g. loaded from revision)
  const prevExternalRef = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (
      externalPriceCents !== undefined &&
      externalPriceCents !== prevExternalRef.current &&
      externalPriceCents !== calc.priceCents
    ) {
      calc.setPrice(externalPriceCents);
    }
    prevExternalRef.current = externalPriceCents;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPriceCents]);

  const handleSliderChange = (_: Event, value: number | number[]) => {
    const m = Array.isArray(value) ? value[0] : value;
    calc.setMargin(m);
    const newPrice = priceFromMargin(calc.referenceCents, m);
    onPriceChange?.(newPrice);
  };

  const handlePriceInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const euros = parseFloat(e.target.value);
    if (isNaN(euros)) return;
    const cents = Math.round(euros * 100);
    calc.setPrice(cents);
    onPriceChange?.(cents);
  };

  const handleReferenceTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    newType: ProposalReferenceType | null,
  ) => {
    if (!newType || newType === referenceType) return;
    onReferenceTypeChange?.(newType);
  };

  const refTypeLabel = refTotalLabelFor(referenceType);
  const refTotalLabel = `Valor ref. total (${refTypeLabel})`;
  const refTotalTooltip = refTotalTooltipFor(referenceType);

  const toggleDisabled = referenceLocked;
  const costButtonDisabled = !isAdmin;

  const helperBelowRef =
    referenceType === "COST"
      ? "Abaixo do custo — ajusta antes de guardar."
      : `Abaixo do valor de referência ${refTypeLabel} — ajusta antes de guardar.`;
  const helperOk =
    "Este é o único valor editável. Os preços das linhas são referência de catálogo.";

  // PR 6a — IVA label is rounded for display. Math.round = half-away-from-zero.
  const vatPctDisplay =
    vatRate !== undefined ? Math.round(vatRate * 100) : undefined;
  const vatTooltipText = vatRegime
    ? `Regime: ${vatRegime}. IVA aplicado sobre o preço final.`
    : "IVA aplicado sobre o preço final.";

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: calc.isInvalid ? "error.main" : "divider",
        borderRadius: 2,
        p: 2,
        bgcolor: "background.paper",
      }}
    >
      {/* PR 6a — KPI block (opt-in via `role` prop). Sits at the top so screen
          readers and sighted users see margin posture before the controls. */}
      {role !== undefined && (
        <Box mb={1.5}>
          <ProposalKpiBlock kpis={kpis} role={role} />
        </Box>
      )}

      {/* Header: title + reference toggle */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Typography variant="subtitle2" fontWeight={700}>
          Painel de margem
        </Typography>
        <Tooltip
          title={
            toggleDisabled
              ? "Tipo de referência trancado pela revisão anterior"
              : costButtonDisabled
                ? "A opção Custo só está disponível para admin. REV4 é livre para todos."
                : "Escolhe o valor de referência: Rev3, Rev4 ou Custo"
          }
        >
          <span>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={referenceType}
              onChange={handleReferenceTypeChange}
              disabled={toggleDisabled}
              aria-label="Tipo de referência"
            >
              <ToggleButton value="REV3" aria-label="Rev3">
                Rev3
              </ToggleButton>
              <ToggleButton value="REV4" aria-label="Rev4">
                Rev4
              </ToggleButton>
              <ToggleButton
                value="COST"
                aria-label="Custo"
                disabled={costButtonDisabled}
              >
                Custo
              </ToggleButton>
            </ToggleButtonGroup>
          </span>
        </Tooltip>
      </Box>

      {/* Reference total + zone badge */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Tooltip title={refTotalTooltip}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {refTotalLabel}
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {formatCents(calc.referenceCents)} €
            </Typography>
          </Box>
        </Tooltip>
        <Chip
          label={ZONE_LABEL[zone]}
          color={ZONE_COLOR[zone]}
          size="small"
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {/* Margin slider + inline absolute margin */}
      <Box mb={1}>
        <Typography variant="caption" color="text.secondary">
          Margem:{" "}
          <strong>
            {calc.margin.toFixed(1)}% (€ {formatEuros(calc.marginAbsCents)})
          </strong>
          {calc.intent === "price" && (
            <span style={{ marginLeft: 6, fontSize: "0.7rem", color: "#888" }}>
              (calculada pelo preço final)
            </span>
          )}
        </Typography>
        <Slider
          value={calc.margin}
          min={0}
          max={50}
          step={0.5}
          marks={SLIDER_MARKS}
          onChange={handleSliderChange}
          color={calc.isInvalid ? "error" : "primary"}
          sx={{ mt: 0.5, mb: 0 }}
        />
      </Box>

      {/* Final price input + Rev4 avg + (PR 6a) IVA label */}
      <Box display="flex" alignItems="center" gap={2} mt={1.5}>
        <Box flex={1}>
          <Typography variant="caption" color="text.secondary">
            Preço Final da Proposta{" "}
            <strong>(valor apresentado ao cliente)</strong>
          </Typography>
          <TextField
            type="number"
            size="small"
            fullWidth
            value={formatCents(calc.priceCents)}
            onChange={handlePriceInput}
            error={calc.isInvalid}
            helperText={calc.isInvalid ? helperBelowRef : helperOk}
            inputProps={{ min: 0, step: "0.01" }}
          />
        </Box>
        <Box textAlign="right" minWidth={80}>
          <Typography variant="caption" color="text.secondary">
            Rev4 médio
          </Typography>
          <Typography variant="body1" fontWeight={600}>
            {calc.rev4Avg.toFixed(1)}%
          </Typography>
        </Box>
        {/* PR 6a — IVA label. Semantic <button> with Tooltip; replaces the
            previous <Box tabIndex={0}> anti-pattern. Rendered AFTER the
            price input so the tab order is: price → Rev4 → IVA. */}
        {vatPctDisplay !== undefined && (
          <Tooltip title={vatTooltipText} arrow placement="top">
            <Button
              data-testid="price-panel-vat"
              variant="outlined"
              size="small"
              aria-label={`IVA: ${vatPctDisplay}%${
                vatRegime ? ` — regime ${vatRegime}` : ""
              }`}
              sx={{
                minWidth: 0,
                textTransform: "none",
                fontWeight: 600,
                py: 0.5,
                px: 1,
              }}
            >
              IVA: {vatPctDisplay}%
            </Button>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default PricePanel;
